import { isUUID } from '@frontend/rendering/details';
import React, { JSX, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ObjectBreadcrumb from '../object-breadcrumb/object-breadcrumb.component';
import TextBreadcrumb from '../text-breadcrumb/text-breadcrumb.component';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface ViewProps {
    breadcrumbs: JSX.Element[];
    title: string;
}

const divider = <span className='text-brand-muted mx-2 text-sm'>/</span>;

const useBreadcrumbs = (): ViewProps => {
    const location = useLocation();
    const [breadcrumbs, changeBreadcrumbs] = useState<JSX.Element[]>([]);
    const title = useMemo(() => {
        const urlElements = location.pathname.split('/');
        const pageType = urlElements[urlElements.length - 1];
        let expectedIndex = 0;

        if (['detail', 'update'].includes(pageType)) {
            if (!isUUID(urlElements[urlElements.length - 2])) {
                expectedIndex = urlElements.length - 2;
            } else {
                expectedIndex = urlElements.length - 3;
            }
        } else if (['create', 'add'].includes(pageType)) {
            expectedIndex = urlElements.length - 2;
        } else {
            expectedIndex = urlElements.length - 1;
        }
        let result = urlElements[expectedIndex].endsWith('s')
            ? urlElements[expectedIndex].endsWith('ies')
                ? urlElements[expectedIndex].slice(0, -3) + 'y'
                : urlElements[expectedIndex].slice(0, -1)
            : urlElements[expectedIndex];
        result = result + ' ' + (['detail', 'update', 'create', 'add'].includes(pageType) ? pageType : 'overview');
        return result.slice(0, 1).toUpperCase() + result.slice(1).replace(/_|-/g, ' ');
    }, [location]);

    useEffect(() => {
        const breadcrumbs: JSX.Element[] = [];
        let currentPath = window.location.origin;
        breadcrumbs.push(/*home, */ divider);
        const urlElements = location.pathname.split('/');
        urlElements.forEach((path, index) => {
            if (path !== '') {
                const isLast = index === urlElements.length - 1;
                const type = currentPath.split('/').pop();
                currentPath += `/${path}`;

                if (UUID_REGEX.test(path) && type) {
                    breadcrumbs.push(
                        <ObjectBreadcrumb
                            value={path}
                            type={type}
                        />
                    );
                } else {
                    breadcrumbs.push(
                        <TextBreadcrumb
                            value={path}
                            path={currentPath}
                        />
                    );
                }
                if (!isLast) breadcrumbs.push(divider);
            }
        });
        changeBreadcrumbs(breadcrumbs);
    }, [location]);
    return { breadcrumbs, title };
};

export default useBreadcrumbs;
